import { Button, Card, buttonVariants, cn } from '@wundo/ui';
import {
  CircleAlert,
  ClipboardList,
  Clock,
  DoorClosed,
  Hourglass,
  Link2Off,
  Lock,
  Mic,
  MicOff,
  UserX,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';
import type { MeetingDetail } from '@wundo/shared/calendar.ts';
import type {
  MeetingNotetakerIssueKind,
  MeetingPageNotetakerIssue,
} from '@wundo/shared/meetingPage.ts';
import { formatTime } from '@/components/agenda/meetingFormat.ts';
import { m } from '@/paraglide/messages.js';

export interface NotetakerIssueBannerProps {
  issue: MeetingPageNotetakerIssue;
  meeting: MeetingDetail;
  onShareNotes: () => void;
}

interface IssueCopy {
  icon: LucideIcon;
  title: string;
  body: string;
}

/**
 * What went wrong with the notetaker, said once, under the settings. While the
 * meeting runs it points the user into the meeting to unblock it; afterwards it
 * offers their own notes instead. Only "nobody came" and "too little said" ask
 * quietly: there the missing verslag is no surprise.
 */
export function NotetakerIssueBanner({
  issue,
  meeting,
  onShareNotes,
}: NotetakerIssueBannerProps) {
  const live = issue.kind === 'waiting_room' || issue.kind === 'waiting_for_permission';
  const quiet = issue.kind === 'nobody_joined' || issue.kind === 'too_little_said';
  const copy = issueCopy(issue, meeting);
  const Icon = copy.icon;

  return (
    <Card as='section' tone='quiet' className='mt-4.5'>
      <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
        <span className='grid size-9 shrink-0 place-items-center rounded-full border border-line bg-surface text-ink-secondary'>
          <Icon aria-hidden='true' className='size-4.5' />
        </span>
        <div className='min-w-0 flex-1'>
          <p className='flex items-center gap-2 text-md font-bold text-ink'>
            <span
              aria-hidden='true'
              className={cn(
                'size-2 shrink-0 rounded-full bg-primary',
                live && 'animate-pulse ring-3 ring-line',
              )}
            />
            {copy.title}
          </p>
          <p className='mt-0.5 text-base text-ink-body'>{copy.body}</p>
        </div>
        {live ? (
          meeting.join_url && (
            <a
              className={buttonVariants({ size: 'sm' })}
              href={meeting.join_url}
              target='_blank'
              rel='noreferrer'
            >
              <Video aria-hidden='true' className='size-4' />
              {m.meeting_notetaker_issue_join()}
            </a>
          )
        ) : (
          <Button size='sm' variant={quiet ? 'quiet' : 'primary'} onClick={onShareNotes}>
            <ClipboardList aria-hidden='true' className='size-4' />
            {m.meeting_report_share_notes()}
          </Button>
        )}
      </div>
    </Card>
  );
}

function issueCopy(issue: MeetingPageNotetakerIssue, meeting: MeetingDetail): IssueCopy {
  const after = m.meeting_notetaker_issue_after();
  const kind: MeetingNotetakerIssueKind = issue.kind;
  switch (kind) {
    case 'waiting_room':
      return {
        icon: Hourglass,
        title: m.meeting_notetaker_issue_waiting_room_title(),
        body: [
          waitingRoomWho(meeting),
          issue.gives_up_at
            ? m.meeting_notetaker_issue_waiting_room_until({
                time: formatTime(issue.gives_up_at),
              })
            : null,
        ]
          .filter((part) => part !== null)
          .join(' '),
      };
    case 'waiting_for_permission':
      return {
        icon: Mic,
        title: m.meeting_notetaker_issue_waiting_for_permission_title(),
        body: m.meeting_notetaker_issue_waiting_for_permission_body(),
      };
    case 'not_admitted':
      return {
        icon: DoorClosed,
        title: m.meeting_notetaker_issue_not_admitted_title(),
        body: `${m.meeting_notetaker_issue_not_admitted_body()} ${after}`,
      };
    case 'blocked':
      return {
        icon: Lock,
        title: m.meeting_notetaker_issue_blocked_title(),
        body: `${m.meeting_notetaker_issue_blocked_body()} ${after}`,
      };
    case 'removed':
      return {
        icon: UserX,
        title: m.meeting_notetaker_issue_removed_title(),
        body: m.meeting_notetaker_issue_removed_body(),
      };
    case 'recording_denied':
      return {
        icon: MicOff,
        title: m.meeting_notetaker_issue_recording_denied_title(),
        body: `${m.meeting_notetaker_issue_recording_denied_body()} ${after}`,
      };
    case 'link_broken':
      return {
        icon: Link2Off,
        title: m.meeting_notetaker_issue_link_broken_title(),
        body: `${m.meeting_notetaker_issue_link_broken_body()} ${after}`,
      };
    case 'nobody_joined':
      return {
        icon: Users,
        title: m.meeting_notetaker_issue_nobody_joined_title(),
        body: m.meeting_notetaker_issue_nobody_joined_body(),
      };
    case 'too_little_said':
      return {
        icon: Clock,
        title: m.meeting_notetaker_issue_too_little_said_title(),
        body: m.meeting_notetaker_issue_too_little_said_body(),
      };
    case 'failed':
      return {
        icon: CircleAlert,
        title: m.meeting_notetaker_issue_failed_title(),
        body: `${m.meeting_notetaker_issue_failed_body()} ${after}`,
      };
  }
}

/* Who has to let the notetaker in: the reader, the organiser by name, or just "the organiser". */
function waitingRoomWho(meeting: MeetingDetail): string {
  if (meeting.is_organizer) return m.meeting_notetaker_issue_waiting_room_body_you();
  if (meeting.organizer_label) {
    return m.meeting_notetaker_issue_waiting_room_body_organizer({
      name: meeting.organizer_label,
    });
  }
  return m.meeting_notetaker_issue_waiting_room_body();
}
